<template>
  <div class="upload-container">
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <VideoFileStatus
      v-if="videoFile"
      :video-file="videoFile"
    />
    <div v-if="videoFileId" class="action-buttons">
      <button v-if="isSuperuser() || videoFile?.stream_url" class="action-button view" @click="handleView">
        <font-awesome-icon icon="eye" />
      </button>
      <button v-if="isSuperuser()" class="action-button edit" @click="handleEdit">
        <font-awesome-icon icon="edit" />
      </button>
      <button class="action-button delete" @click="handleDelete">
        <font-awesome-icon icon="trash" />
      </button>
    </div>
    <FileUpload
      v-else
      :max-file-size="props.maxFileSize"
      accept="video/*,.aivu"
      :folder="props.folder"
      @success="handleUploadSuccess"
    />
  </div>
</template>

<script setup>
import { ref, defineModel, computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';
import { FontAwesomeIcon } from '@/scripts/icons';
import FileUpload from './FileUpload.vue';
import VideoFileStatus from './VideoFileStatus.vue';
import { createChannelVideoFile } from '@/scripts/api/channelApi';
import { getVideoFile, videoFileKeys } from '@/scripts/api/videoFileApi';

const videoFileId = defineModel({ type: String });
const error = ref('');
const router = useRouter();
const queryClient = useQueryClient();

const videoFileQueryKey = computed(() => videoFileKeys.getVideoFile(videoFileId.value));
const isVideoFileIdValid = computed(() => !!videoFileId.value);

const { data: videoFile } = useQuery({
  queryKey: videoFileQueryKey,
  queryFn: () => getVideoFile(videoFileId.value),
  enabled: isVideoFileIdValid,
  staleTime: 60000,
  onError: (err) => handleApiError(err, 'fetching video file')
});

const props = defineProps({
  folder: {
    type: String,
    required: true
  },
  maxFileSize: {
    type: Number,
    default: 50 * 1024 * 1024 // 50MB
  },
  channelId: {
    type: String,
    required: true
  }
});

const createVideoFileMutation = useMutation({
  mutationFn: (videoFileData) => createChannelVideoFile(props.channelId, videoFileData),
  onSuccess: (data) => {
    videoFileId.value = data.id;
    queryClient.invalidateQueries(videoFileKeys.getVideoFile(data.id));
  },
  onError: (err) => {
    handleApiError(err, 'creating video file');
    error.value = 'Failed to create video file. Please try again.';
  },
});

const handleUploadSuccess = (fileData) => {
  error.value = '';
  const requestBody = {
    file_name: fileData.file_name,
    file_url: fileData.url,
    file_size: fileData.file_size,
    ...fileData.video_metadata
  };
  createVideoFileMutation.mutate(requestBody);
};

const handleView = () => {
  if (videoFileId.value) {
    router.push(`/videofiles/${videoFileId.value}`);
  }
};

const handleEdit = () => {
  if (videoFileId.value) {
    router.push(`/admin/videofiles/${videoFileId.value}/edit`);
  }
};

const handleDelete = () => {
  videoFileId.value = null;
};

</script>

<style scoped>
@import '@/styles/file-upload.css';
</style>
